import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Typography,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import {
  DEFAULT_MAX_UPLOAD_BYTES,
  cloudflareWorkersApiRef,
  formatBytes,
  processFilesToZipBlob,
  processZipFileToZipBlob,
} from '@internal/plugin-cloudflare-workers';
import { useCallback, useRef, useState } from 'react';

export const ProjectUpload = (props: FieldExtensionComponentProps<string>) => {
  const {
    onChange,
    schema: {
      title = 'Project Upload',
      description = 'Upload a folder or zip file',
    },
    uiSchema,
  } = props;

  const configApi = useApi(configApiRef);
  const cloudflareWorkersApi = useApi(cloudflareWorkersApiRef);

  // ui:options.maxSize, when set, only ever tightens the config-driven limit
  // (a template can ask for a stricter cap; it can't raise the backend's).
  const configuredMaxSize =
    configApi.getOptionalNumber(
      'integrations.cloudflare.vibeUploads.maxUploadBytes',
    ) ?? DEFAULT_MAX_UPLOAD_BYTES;
  const uiMaxSize = uiSchema?.['ui:options']?.maxSize as number | undefined;
  const maxSize =
    uiMaxSize !== undefined
      ? Math.min(uiMaxSize, configuredMaxSize)
      : configuredMaxSize;

  const sizeLimitHint =
    (uiSchema?.['ui:options']?.sizeLimitHint as string) ??
    'Try removing large or unnecessary files before uploading.';
  const skipDefaultIgnores =
    (uiSchema?.['ui:options']?.skipDefaultIgnores as boolean) ?? false;
  const skipGitignore =
    (uiSchema?.['ui:options']?.skipGitignore as boolean) ?? false;

  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (
      label: string,
      work: () => Promise<{
        blob: Blob;
        sizeBytes: number;
        fileCount: number;
      }>,
    ) => {
      setError(null);
      setLoading(true);
      setStatus(label);

      try {
        const { blob, sizeBytes, fileCount } = await work();

        if (sizeBytes > maxSize) {
          setError(
            `Upload is ${formatBytes(sizeBytes)}, exceeds ${formatBytes(
              maxSize,
            )} limit. ${sizeLimitHint}`,
          );
          setLoading(false);
          setStatus(null);
          onChange('');
          return;
        }

        setStatus('Uploading...');
        const { uploadId } = await cloudflareWorkersApi.uploadProject(blob);

        setStatus(`${fileCount} files (${formatBytes(sizeBytes)}) uploaded`);
        setLoading(false);
        onChange(uploadId);
      } catch (err) {
        setError(
          `Failed to process upload: ${
            err instanceof Error ? err.message : err
          }`,
        );
        setLoading(false);
        setStatus(null);
        onChange('');
      }
    },
    [onChange, maxSize, sizeLimitHint, cloudflareWorkersApi],
  );

  const processFiles = useCallback(
    (files: FileList) =>
      handleUpload('Processing...', () =>
        processFilesToZipBlob(files, { skipDefaultIgnores, skipGitignore }),
      ),
    [handleUpload, skipDefaultIgnores, skipGitignore],
  );

  const handleZipUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return undefined;
      return handleUpload('Reading zip...', () =>
        processZipFileToZipBlob(file, { skipDefaultIgnores, skipGitignore }),
      );
    },
    [handleUpload, skipDefaultIgnores, skipGitignore],
  );

  return (
    <Box marginTop={2} marginBottom={1}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {description}
      </Typography>

      <Paper variant="outlined" style={{ padding: 16 }}>
        <Box display="flex" gridGap="1em" alignItems="center">
          <input
            ref={folderInputRef}
            type="file"
            style={{ display: 'none' }}
            {...({ webkitdirectory: '', directory: '' } as any)}
            multiple
            onChange={e => {
              if (e.target.files) processFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={e => {
              handleZipUpload(e);
              e.target.value = '';
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={() => folderInputRef.current?.click()}
            disabled={loading}
          >
            Select Folder
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => zipInputRef.current?.click()}
            disabled={loading}
          >
            Select Zip
          </Button>
        </Box>

        {loading && (
          <Box marginTop={2}>
            <LinearProgress />
          </Box>
        )}

        {status && !error && (
          <Box marginTop={1}>
            <Typography variant="body2" color="textSecondary">
              {status}
            </Typography>
          </Box>
        )}

        {error && (
          <Box marginTop={1}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
